import { useState, useEffect } from 'react'
import axios from 'axios'
import {
  Card, CardBody, Row, Col, Spinner, Badge,
  Nav, NavItem, NavLink
} from 'reactstrap'
import { Star, AlertCircle, CheckCircle, Clock, MessageSquare, ThumbsUp, User } from 'react-feather'

const BASE   = process.env.REACT_APP_BASEURL
const getToken = () => localStorage.getItem(process.env.REACT_APP_USERTOKEN)
const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } })

// ─── Star display (read-only) ─────────────────────────────────────────────────
const StarDisplay = ({ value, size = 16 }) => {
  const num = Number(value) || 0
  return (
    <span className="d-inline-flex align-items-center" style={{ gap: '3px' }}>
      {[1, 2, 3, 4, 5].map(n => (
        <Star
          key={n}
          size={size}
          fill={n <= num ? '#ff9f43' : 'none'}
          stroke={n <= num ? '#ff9f43' : '#d0d2d6'}
        />
      ))}
      <span className="font-small-2 font-weight-bold ml-50 text-dark">({num}/5)</span>
    </span>
  )
}

// ─── Summary KPI Metrics Card ──────────────────────────────────────────────────
const SummaryMetrics = ({ summary }) => {
  if (!summary) return null
  const { avg_rating, recommend_pct, counts } = summary

  return (
    <Row className="mb-2 font-inter">
      {/* 1. Average Rating */}
      <Col xs={12} md={4} className="mb-1 mb-md-0">
        <Card className="h-100 shadow-none border" style={{ borderRadius: '10px', background: '#fafafa' }}>
          <CardBody className="p-1 text-center">
            <span className="font-small-2 font-weight-bolder text-uppercase text-primary d-block mb-50">
              Average Rating
            </span>
            {avg_rating ? (
              <div>
                <h2 className="font-weight-bolder text-dark mb-25" style={{ fontSize: '28px' }}>
                  {avg_rating}
                </h2>
                <StarDisplay value={Math.round(avg_rating)} size={18} />
              </div>
            ) : (
              <span className="text-muted font-small-3 d-block py-50">No ratings yet</span>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* 2. Would Recommend */}
      <Col xs={12} md={4} className="mb-1 mb-md-0">
        <Card className="h-100 shadow-none border" style={{ borderRadius: '10px', background: '#fafafa' }}>
          <CardBody className="p-1 text-center">
            <span className="font-small-2 font-weight-bolder text-uppercase text-success d-block mb-50">
              Would Recommend
            </span>
            {recommend_pct !== null && recommend_pct !== undefined ? (
              <div>
                <h2 className="font-weight-bolder text-success mb-25" style={{ fontSize: '28px' }}>
                  {recommend_pct}%
                </h2>
                <small className="text-muted font-small-2">of dietitians recommend to clients</small>
              </div>
            ) : (
              <span className="text-muted font-small-3 d-block py-50">No data yet</span>
            )}
          </CardBody>
        </Card>
      </Col>

      {/* 3. Response Counts */}
      <Col xs={12} md={4}>
        <Card className="h-100 shadow-none border" style={{ borderRadius: '10px', background: '#fafafa' }}>
          <CardBody className="p-1 text-center">
            <span className="font-small-2 font-weight-bolder text-uppercase text-secondary d-block mb-50">
              Total Responses
            </span>
            <div className="d-flex justify-content-around align-items-center mt-50">
              <div>
                <span className="font-weight-bolder text-success d-block" style={{ fontSize: '20px' }}>
                  {counts?.completed ?? 0}
                </span>
                <span className="font-small-1 text-muted">Completed</span>
              </div>
              <div className="border-left pl-1">
                <span className="font-weight-bolder text-warning d-block" style={{ fontSize: '20px' }}>
                  {counts?.pending ?? 0}
                </span>
                <span className="font-small-1 text-muted">Pending</span>
              </div>
              <div className="border-left pl-1">
                <span className="font-weight-bolder text-danger d-block" style={{ fontSize: '20px' }}>
                  {counts?.flagged ?? 0}
                </span>
                <span className="font-small-1 text-muted">Flagged</span>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
    </Row>
  )
}

// ─── Individual Dietitian Response Card ───────────────────────────────────────
const ResponseCard = ({ survey }) => {
  const isFlagged = survey.status === 'flagged'
  const answers   = survey.answers || {}
  const rating    = answers.q3_rating ? parseInt(answers.q3_rating, 10) : null

  return (
    <Card
      className="shadow-none border mb-1 font-inter"
      style={{
        borderRadius: '10px',
        borderColor: isFlagged ? '#fca5a5' : '#e2e8f0',
        background: isFlagged ? '#fffbfb' : '#ffffff'
      }}
    >
      <CardBody className="p-1">
        {/* RD Info Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center pb-75 mb-1 border-bottom">
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle d-flex align-items-center justify-content-center mr-1"
              style={{ width: '38px', height: '38px', background: isFlagged ? '#fee2e2' : '#ede9fe', color: isFlagged ? '#dc2626' : '#7367f0' }}
            >
              <User size={18} />
            </div>
            <div>
              <h6 className="font-weight-bolder text-dark mb-0 font-inter" style={{ fontSize: '14.5px' }}>
                {survey.rd_name || 'Dietitian'}
              </h6>
              <small className="text-muted font-small-2">
                Accepted: {survey.accepted_at ?? '—'}
                {survey.completed_at && ` &bull; Completed: ${survey.completed_at}`}
              </small>
            </div>
          </div>
          <div className="mt-50 mt-sm-0">
            {isFlagged ? (
              <Badge color="light-danger" pill className="font-small-1 font-weight-bold px-1 py-50">
                <AlertCircle size={12} className="mr-25" /> Sample Not Received
              </Badge>
            ) : (
              <Badge color="light-success" pill className="font-small-1 font-weight-bold px-1 py-50">
                <CheckCircle size={12} className="mr-25" /> Feedback Completed
              </Badge>
            )}
          </div>
        </div>

        {/* Flagged Alert Banner */}
        {isFlagged && (
          <div className="p-75 mb-1 rounded bg-light-danger">
            <p className="font-small-3 text-danger mb-0">
              The dietitian reported that their free samples never arrived. Please check with your fulfillment / shipping provider to verify delivery.
            </p>
          </div>
        )}

        {/* Answers Content */}
        {!isFlagged && (
          <Row>
            {/* Overall Rating */}
            {rating && (
              <Col xs={12} sm={6} md={4} className="mb-1">
                <span className="font-small-1 font-weight-bolder text-uppercase text-secondary d-block mb-25">
                  Overall Rating
                </span>
                <StarDisplay value={rating} size={15} />
              </Col>
            )}

            {/* Recommend */}
            {answers.q5_recommend && (
              <Col xs={12} sm={6} md={4} className="mb-1">
                <span className="font-small-1 font-weight-bolder text-uppercase text-secondary d-block mb-25">
                  Recommend to Clients
                </span>
                <div className="d-flex align-items-center">
                  <ThumbsUp size={14} className="text-primary mr-50" />
                  <span className="font-small-3 font-weight-bold text-dark">{answers.q5_recommend}</span>
                </div>
              </Col>
            )}

            {/* Future Collaboration */}
            {answers.q6_work_again && (
              <Col xs={12} sm={6} md={4} className="mb-1">
                <span className="font-small-1 font-weight-bolder text-uppercase text-secondary d-block mb-25">
                  Future Partnership
                </span>
                <span className="font-small-3 font-weight-bold text-dark">{answers.q6_work_again}</span>
              </Col>
            )}

            {/* Comments Box */}
            {answers.q4_comment && answers.q4_comment !== '(skipped)' && (
              <Col xs={12} className="mt-50">
                <span className="font-small-1 font-weight-bolder text-uppercase text-secondary d-block mb-25">
                  Dietitian Feedback & Comments
                </span>
                <div className="p-75 rounded bg-light border">
                  <div className="d-flex align-items-start">
                    <MessageSquare size={14} className="text-muted mr-50 mt-25 flex-shrink-0" />
                    <p className="font-small-3 text-dark mb-0 font-italic" style={{ lineHeight: '1.45' }}>
                      &ldquo;{answers.q4_comment}&rdquo;
                    </p>
                  </div>
                </div>
              </Col>
            )}
          </Row>
        )}
      </CardBody>
    </Card>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────
const SampleFeedbackView = ({ campaignId }) => {
  const [loading, setLoading]       = useState(true)
  const [summary, setSummary]       = useState(null)
  const [responses, setResponses]   = useState([])
  const [activeFilter, setActiveFilter] = useState('all')
  const [error, setError]           = useState(null)

  useEffect(() => {
    if (!campaignId) return
    setLoading(true)
    Promise.all([
      axios.get(`${BASE}brand/sample-feedback?campaign_id=${campaignId}`, authHeaders()),
      axios.get(`${BASE}brand/sample-feedback/summary?campaign_id=${campaignId}`, authHeaders())
    ])
      .then(([listRes, summaryRes]) => {
        setResponses(listRes.data?.data || [])
        setSummary(summaryRes.data)
      })
      .catch(() => setError('Could not load feedback data.'))
      .finally(() => setLoading(false))
  }, [campaignId])

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-4 font-inter">
        <Spinner size="md" color="primary" className="mr-1" />
        <span className="text-muted font-small-3">Loading sample feedback metrics...</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="d-flex align-items-center p-2 text-danger font-inter">
        <AlertCircle size={18} className="mr-75" />
        <span>{error}</span>
      </div>
    )
  }

  const filteredResponses = responses.filter(r => {
    if (activeFilter === 'completed') return r.status === 'completed'
    if (activeFilter === 'flagged') return r.status === 'flagged'
    return true
  })

  return (
    <div className="font-inter">
      {/* 1. Summary KPI Cards */}
      <SummaryMetrics summary={summary} />

      {/* 2. Sub-tabs / Filter */}
      {responses.length > 0 && (
        <div className="d-flex justify-content-between align-items-center mb-1">
          <Nav pills className="mb-0">
            <NavItem>
              <NavLink
                active={activeFilter === 'all'}
                onClick={() => setActiveFilter('all')}
                style={{ cursor: 'pointer', padding: '6px 14px', fontSize: '13px' }}
              >
                All Responses ({responses.length})
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                active={activeFilter === 'completed'}
                onClick={() => setActiveFilter('completed')}
                style={{ cursor: 'pointer', padding: '6px 14px', fontSize: '13px' }}
              >
                Completed ({responses.filter(r => r.status === 'completed').length})
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                active={activeFilter === 'flagged'}
                onClick={() => setActiveFilter('flagged')}
                style={{ cursor: 'pointer', padding: '6px 14px', fontSize: '13px' }}
              >
                Delivery Issues ({responses.filter(r => r.status === 'flagged').length})
              </NavLink>
            </NavItem>
          </Nav>
        </div>
      )}

      {/* 3. Individual Responses List */}
      <div>
        {filteredResponses.length === 0 ? (
          <Card className="shadow-none border" style={{ borderRadius: '10px', borderStyle: 'dashed' }}>
            <CardBody className="text-center py-4">
              <Clock size={32} className="text-muted mb-1" />
              <h6 className="font-weight-bolder text-dark mb-50">No Feedback Responses Yet</h6>
              <p className="text-muted font-small-3 mb-0" style={{ maxWidth: '420px', margin: '0 auto' }}>
                Feedback questionnaires are automatically dispatched to participating dietitians 14 days after sample acceptance. Responses will be displayed here in real-time.
              </p>
            </CardBody>
          </Card>
        ) : (
          filteredResponses.map(s => <ResponseCard key={s.survey_id} survey={s} />)
        )}
      </div>
    </div>
  )
}

export default SampleFeedbackView
